import { watch } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { useToast } from 'primevue/usetoast';

/**
 * Composable that watches for flash messages from Laravel and shows toasts
 *
 * Usage in Laravel:
 *   return back()->with('success', 'Mensaje de éxito');
 *   return back()->with('error', 'Mensaje de error');
 *   return back()->with('warning', 'Mensaje de advertencia');
 *   return back()->with('info', 'Mensaje informativo');
 *   return back()->with('status', 'Mensaje de estado'); // Also treated as success
 */
export function useFlashMessages() {
    const page = usePage();
    const toast = useToast();

    const showFlashMessages = () => {
        const flash = page.props?.flash;

        if (!flash) return;

        // Success message
        if (flash.success) {
            toast.add({
                severity: 'success',
                summary: 'Éxito',
                detail: flash.success,
                life: 4000,
            });
        }

        // Status message (treated as success - used by Laravel's password reset, etc.)
        if (flash.status) {
            toast.add({
                severity: 'success',
                summary: 'Éxito',
                detail: flash.status,
                life: 4000,
            });
        }

        // Error message
        if (flash.error) {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: flash.error,
                life: 6000,
            });
        }

        // Warning message
        if (flash.warning) {
            toast.add({
                severity: 'warn',
                summary: 'Advertencia',
                detail: flash.warning,
                life: 5000,
            });
        }

        // Info message
        if (flash.info) {
            toast.add({
                severity: 'info',
                summary: 'Información',
                detail: flash.info,
                life: 4000,
            });
        }
    };

    // Watch for changes in flash messages
    watch(
        () => page.props?.flash,
        () => {
            showFlashMessages();
        },
        { immediate: true, deep: true }
    );

    return {
        toast,
        showFlashMessages,
    };
}
